/**
 * ForecastDayRow — dense single-row forecast day entry (DASH-FC1 rewrite).
 *
 * Layout (fits 380px with no horizontal overflow):
 *   [day label] [4-seg meter] [hi/lo] [precip %] [wind] [moon glyph] [chevron]
 *
 * Rating wiring: `ConditionMeter` tier is a LOCAL `LEVEL_TO_TIER` lookup off
 * the engine's `level` (QualityLevel) — NEVER a re-bucket of the raw `score`.
 * This mirrors `ConditionsWidget.tsx:46-51` (Reviewer ruling A3).
 *
 * Expanded detail: NEAR days show `ConditionSparkline` (no sightings); FAR days
 * show a single tier mark + the honesty line "hourly detail available within
 * 48 hours". Both show the Temp/Wind/Hunt window/Pressure grid.
 *
 * "Hunt window" is the display label — "crepuscular" is NEVER surfaced.
 *
 * Scope fence (RULE-276): imports no `@/services` or `@/stores`.
 * Imports: only shared primitives + local `MoonTerminatorGlyph`.
 */

import { Thermometer, Wind, Sunrise, Gauge, Droplets } from 'lucide-react';

import { ConditionMeter } from '@/components/common/ConditionMeter';
import { CONDITION_RATING_DISPLAY } from '@/components/common/conditionRatingDisplay';
import { ConditionSparkline } from '@/components/common/ConditionSparkline';
import { DataCockpit } from '@/components/common/DataCockpit';
import { ExpandChevron } from '@/components/common/ExpandChevron';
import { MoonTerminatorGlyph } from '@/components/forecast/MoonTerminatorGlyph';
import type { QualityLevel, FavorabilityPoint } from '@/types/conditions';
import type { DataCockpitCell } from '@/types/data-cockpit';

// ---------------------------------------------------------------------------
// LEVEL_TO_TIER — mirrors ConditionsWidget.tsx:46-51 (Reviewer ruling A3).
// Maps engine QualityLevel to the 4-segment meter ordinal (1–4).
// NEVER derived from the raw score — the score→level step is the engine's concern.
// ---------------------------------------------------------------------------

const LEVEL_TO_TIER: Record<QualityLevel, 1 | 2 | 3 | 4> = {
  poor:      1,
  fair:      2,
  good:      3,
  excellent: 4,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ForecastDayRowProps {
  /** Day label — "Today", "Mon 15", etc. */
  dayLabel: string;
  /** Engine-derived quality level (internal key). */
  level: QualityLevel;
  /**
   * Engine-derived continuous score (0–100). Part of the row contract but
   * intentionally NOT consumed for rendering — the meter tier derives from
   * `level` only (see LEVEL_TO_TIER). Retained so callers pass the full engine
   * row and the divergent-fixture tests can assert tier independence from score.
   */
  score: number;
  /** Whether this day is NEAR (hourly horizon reaches civil dusk). */
  isNear: boolean;
  /** Temperature high °F (from daily aggregates). */
  tempMax: number;
  /** Temperature low °F (from daily aggregates). */
  tempMin: number;
  /** Precipitation probability 0–100. */
  precipPct: number;
  /** Wind speed mph. */
  windSpeed: number;
  /** Wind direction abbreviation. */
  windDir: string;
  /** Moon illumination 0–100. */
  moonIllumination: number;
  /** Moon phase value 0–1 (SunCalc raw). */
  moonPhaseValue: number;
  /** Civil dawn time formatted (for expanded grid "Hunt window" cell). */
  civilDawnTime: string;
  /** Civil dusk time formatted (for expanded grid "Hunt window" cell). */
  civilDuskTime: string;
  /** Pressure inHg (for expanded grid "Pressure" cell). */
  pressureInHg: number;
  /** Pressure trend word (for expanded grid "Pressure" cell). */
  pressureTrend: string;
  /** Precipitation volume in inches (for expanded thermal area). */
  precipitationVolume?: number;
  /**
   * Favorability series for NEAR-day sparkline
   * (computeFavorabilityByPeriod output, no sightings).
   */
  sparklinePoints?: FavorabilityPoint[];
  /** Whether this row is currently expanded (parent controls accordion state). */
  isExpanded: boolean;
  /** Called when the user toggles expand/collapse. */
  onToggle: () => void;
  /**
   * Optional row index — when provided, generates per-index data-testid attributes:
   * `forecast-day-{index}`, `forecast-day-header-{index}`, `forecast-day-expanded-{index}`.
   * When omitted uses generic `forecast-row` / `forecast-row-expanded`.
   */
  index?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ForecastDayRow({
  dayLabel,
  level,
  isNear,
  tempMax,
  tempMin,
  precipPct,
  windSpeed,
  windDir,
  moonIllumination,
  moonPhaseValue,
  civilDawnTime,
  civilDuskTime,
  pressureInHg,
  pressureTrend,
  precipitationVolume,
  sparklinePoints,
  isExpanded,
  onToggle,
  index,
}: ForecastDayRowProps) {
  const hasIndex = index !== undefined;
  const rowTestId   = hasIndex ? `forecast-day-${index}` : 'forecast-row';
  const headerTestId = hasIndex ? `forecast-day-header-${index}` : undefined;
  const expandedTestId = hasIndex ? `forecast-day-expanded-${index}` : 'forecast-row-expanded';

  // Map QualityLevel → meter tier (1–4) via the local lookup.
  const tier = LEVEL_TO_TIER[level];
  // Map QualityLevel → display word + color token via the shared display map.
  const { word: ratingWord, token: colorToken } = CONDITION_RATING_DISPLAY[level];

  // Precipitation badge for the expanded thermal cell.
  const precipExpandedContent =
    precipPct > 0 ? (
      <div
        style={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 2 }}
        data-testid="forecast-precip-expanded"
      >
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.1em',
            color: 'var(--color-text-muted, #6e6a63)',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          PRECIP
        </span>
        <span
          style={{ fontSize: 12, color: 'var(--color-text-muted, #6e6a63)', fontFamily: 'DM Sans, sans-serif' }}
        >
          {precipPct}%
          {precipitationVolume !== undefined && precipitationVolume > 0
            ? ` · ${precipitationVolume.toFixed(1)} in`
            : null}
        </span>
      </div>
    ) : undefined;

  const cockpitCells: DataCockpitCell[] = [
    {
      icon: <Thermometer size={12} />,
      primary: `${Math.round(tempMax)}°`,
      secondary: `Lo ${Math.round(tempMin)}°`,
      label: 'TEMP',
      testId: hasIndex ? `forecast-score-bar-${index}` : 'forecast-cockpit-temp',
      badge: precipExpandedContent,
    },
    {
      icon: <Wind size={12} />,
      // No inline "mph" in the collapsed row; in the expanded grid we include it.
      primary: `${Math.round(windSpeed)} mph`,
      secondary: windDir,
      label: 'WIND',
      testId: 'forecast-cockpit-wind',
    },
    {
      icon: <Sunrise size={12} />,
      primary: civilDawnTime,
      secondary: civilDuskTime,
      // "Hunt window" is the display rename — NEVER "crepuscular" (ADR-017).
      label: 'HUNT WINDOW',
      testId: 'forecast-cockpit-hunt-window',
    },
    {
      icon: <Gauge size={12} />,
      primary: `${pressureInHg.toFixed(2)}`,
      secondary: `${pressureTrend} trend`,
      label: 'PRESSURE',
      testId: 'forecast-cockpit-pressure',
    },
  ];

  return (
    <div
      className="border-b border-[var(--color-border-subtle)] last:border-b-0"
      data-testid={rowTestId}
    >
      {/* ---- Collapsed header ---- */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left py-3 px-4 flex items-center gap-2 hover:bg-[var(--color-elevated)] transition-colors"
        aria-expanded={isExpanded}
        data-testid={headerTestId}
      >
        {/* Day label */}
        <div className="w-14 flex-shrink-0">
          <p className="font-bold text-[var(--color-text-primary)] text-body-sm leading-tight">
            {dayLabel}
          </p>
        </div>

        {/* 4-segment condition meter (tier derived from level, not score) */}
        <div className="flex-shrink-0">
          <ConditionMeter tier={tier} colorToken={colorToken} />
        </div>

        {/* Hi/lo + precip probability */}
        <div className="flex-1 min-w-0 text-body-xs text-[var(--color-text-muted)]">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{Math.round(tempMax)}° / {Math.round(tempMin)}°</span>
            {precipPct > 0 && (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: 'var(--color-text-muted, #9a9590)' }}
                data-testid="forecast-precip-probability"
              >
                <Droplets size={12} aria-hidden="true" />
                {precipPct}%
              </span>
            )}
          </div>
        </div>

        {/* Wind — direction + speed, NO inline "mph" (width-saving measure per spec) */}
        <div className="flex-shrink-0 text-body-xs text-[var(--color-text-muted)]">
          {windDir} {Math.round(windSpeed)}
        </div>

        {/* Moon glyph — SVG terminator, not emoji */}
        <div className="flex-shrink-0">
          <MoonTerminatorGlyph illumination={moonIllumination} phaseValue={moonPhaseValue} />
        </div>

        {/* Expand chevron — glyph-swap, reduced-motion safe */}
        <div className="flex-shrink-0 text-[var(--color-text-muted)]">
          <ExpandChevron expanded={isExpanded} />
        </div>
      </button>

      {/* ---- Expanded section ---- */}
      {isExpanded && (
        <div
          className="bg-[var(--color-elevated)] pb-3 flex flex-col gap-2"
          data-testid={expandedTestId}
        >
          {/* Rating word header */}
          <div className="flex items-center gap-2 px-4 pt-3">
            <span
              className="text-body-sm font-semibold"
              style={{ color: `var(${colorToken})` }}
              data-testid={hasIndex ? `forecast-rating-word-${index}` : 'forecast-rating-word'}
            >
              {ratingWord}
            </span>
          </div>

          {/* NEAR: sparkline curve above the grid */}
          {isNear && sparklinePoints && sparklinePoints.length > 0 && (
            <div className="px-4" data-testid={hasIndex ? `forecast-near-sparkline-${index}` : 'forecast-near-sparkline'}>
              <p
                className="field-label"
                style={{ fontSize: '10px', letterSpacing: '0.08em', marginBottom: 4 }}
              >
                FAVORABILITY
              </p>
              {/* No sightings, no now-marker — curve only per spec */}
              <ConditionSparkline points={sparklinePoints} />
            </div>
          )}

          {/* FAR: tier mark + honesty line */}
          {!isNear && (
            <div className="px-4 flex flex-col gap-1" data-testid={hasIndex ? `forecast-far-detail-${index}` : 'forecast-far-detail'}>
              <div className="flex items-center gap-2">
                <ConditionMeter tier={tier} colorToken={colorToken} />
                <span className="text-body-xs text-[var(--color-text-muted)]">
                  {ratingWord}
                </span>
              </div>
              {/* Honesty line — verbatim per spec, never a curve */}
              <p
                className="text-body-xs text-[var(--color-text-muted)]"
                data-testid={hasIndex ? `forecast-honesty-line-${index}` : 'forecast-honesty-line'}
              >
                hourly detail available within 48 hours
              </p>
            </div>
          )}

          {/* Temp / Wind / Hunt window / Pressure grid */}
          <DataCockpit cells={cockpitCells} />
        </div>
      )}
    </div>
  );
}
